import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../lib/icons";
import { cn } from "./ui";

export type TaskState = "running" | "done" | "error";

/** A small live number shown in the modal on completion — "Остаток", "Себестоимость" etc. right
 * after a sync finishes, so the popup answers "so what's the warehouse look like now" instead of
 * just "done". */
export type ProgressStat = { label: string; value: string; icon: string; danger?: boolean };

const STATE_CHIP: Record<TaskState, string> = {
  done: "bg-ok-weak text-ok",
  error: "bg-danger-weak text-danger",
  running: "bg-accent-weak text-accent",
};

/** Shared modal card for long tasks — data refresh and file export use the same look. Flat,
 * minimal surface (2026-09-28): tinted status chip, thin progress bar, frosted backdrop. */
export function ProgressCard({
  title,
  state,
  pct,
  stage,
  detail,
  stats,
  onClose,
  closable = true,
}: {
  title: string;
  state: TaskState;
  pct?: number; // undefined => indeterminate bar
  stage?: string | null;
  detail?: ReactNode;
  stats?: ProgressStat[];
  onClose?: () => void;
  closable?: boolean;
}) {
  // Portalled to <body>: the sticky header (where DataStatus renders this) has backdrop-blur, and a
  // backdrop-filter ancestor turns `fixed inset-0` into "fill the 56px header" — the modal ended up
  // squashed against the top of the page.
  return createPortal(
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/30 p-4 backdrop-blur-sm animate-fade-in"
      onClick={closable ? onClose : undefined}
    >
      <div
        className="relative w-full max-w-sm origin-center overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-pop animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative mb-3 flex items-center gap-2.5">
          <span
            className={cn(
              "grid h-9 w-9 flex-none place-items-center rounded-full",
              STATE_CHIP[state],
            )}
          >
            {state === "done" ? (
              <Icon name="check" size={18} />
            ) : state === "error" ? (
              <Icon name="x" size={18} />
            ) : (
              <Icon name="refresh" size={18} className="animate-spin" />
            )}
          </span>
          <div className="min-w-0">
            <div className="text-base font-semibold">{title}</div>
            {stage && <div className="truncate text-xs text-text-2">{stage}</div>}
          </div>
          {closable && (
            <button
              type="button"
              onClick={onClose}
              title="Закрыть"
              className="ml-auto grid h-8 w-8 flex-none place-items-center rounded-lg text-text-2 hover:bg-surface-2 hover:text-text"
            >
              <Icon name="x" size={16} />
            </button>
          )}
        </div>

        <div className="relative h-1.5 overflow-hidden rounded-full bg-surface-2">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              state === "error" ? "bg-danger" : state === "done" ? "bg-ok" : "bg-accent",
              pct === undefined && state === "running" && "animate-shimmer bg-[length:200%_100%]",
            )}
            style={{ width: pct === undefined ? "100%" : `${Math.max(4, Math.min(100, pct))}%` }}
          />
        </div>
        {pct !== undefined && (
          <div className="relative mt-1 text-right text-xs tnum text-text-2">{Math.round(pct)}%</div>
        )}

        {detail && <div className="relative mt-3 text-xs text-text-2">{detail}</div>}

        {stats && stats.length > 0 && (
          <div className="relative mt-4 grid grid-cols-3 gap-2">
            {stats.map((s) => (
              <div key={s.label} className="rounded-lg border border-border px-2.5 py-2">
                <Icon name={s.icon} size={14} className={s.danger ? "text-danger" : "text-muted"} />
                <div className="mt-1.5 truncate text-sm font-bold tnum">{s.value}</div>
                <div className="truncate text-2xs text-text-2">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
